(() => {
  const SIZE_CHART = [
    {
      key: 'S',
      label: 'Small (S)',
      name: 'Small',
      chest: { min: 37, max: 39 },
      waist: { min: 30, max: 32 },
      inseam: { min: 28, max: 28 },
    },
    {
      key: 'M',
      label: 'Medium (M)',
      name: 'Medium',
      chest: { min: 39, max: 41 },
      waist: { min: 32, max: 34 },
      inseam: { min: 29, max: 30 },
    },
    {
      key: 'L',
      label: 'Large (L)',
      name: 'Large',
      chest: { min: 41, max: 43 },
      waist: { min: 34, max: 36 },
      inseam: { min: 31, max: 31 },
    },
    {
      key: 'XL',
      label: 'Extra Large (XL)',
      name: 'Extra Large',
      chest: { min: 43, max: 45 },
      waist: { min: 36, max: 38 },
      inseam: { min: 32, max: 33 },
    },
    {
      key: '2XL',
      label: 'Double Extra Large (2XL)',
      name: 'Double Extra Large',
      chest: { min: 45, max: 48 },
      waist: { min: 39, max: 41 },
      inseam: { min: 34, max: 36 },
    },
  ];

  const VARIANT_SIZE_ALIASES = {
    S: ['s', 'small'],
    M: ['m', 'medium'],
    L: ['l', 'large'],
    XL: ['xl', 'x-large', 'extra large', 'extra-large'],
    '2XL': ['2xl', 'xxl', 'double extra large', 'double-extra-large'],
  };

  const PROFILE_SETTINGS = {
    male: {
      chestOffset: 8,
      weightBaseline: 170,
      weightChestFactor: 0.025,
      chestAdjustmentMin: -1,
      chestAdjustmentMax: 1.75,
      inseamBands: [
        { maxHeight: 67, inseam: 28 },
        { maxHeight: 70, inseam: 30 },
        { maxHeight: 73, inseam: 31 },
        { maxHeight: 76, inseam: 33 },
        { maxHeight: Infinity, inseam: 35 },
      ],
    },
    female: {
      chestOffset: 7.5,
      weightBaseline: 145,
      weightChestFactor: 0.02,
      chestAdjustmentMin: -0.75,
      chestAdjustmentMax: 1.5,
      inseamBands: [
        { maxHeight: 65, inseam: 28 },
        { maxHeight: 68, inseam: 30 },
        { maxHeight: 71, inseam: 31 },
        { maxHeight: 74, inseam: 33 },
        { maxHeight: Infinity, inseam: 35 },
      ],
    },
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const formatNumber = (value) => (Number.isInteger(value) ? String(value) : value.toFixed(1));

  const formatRange = (range) => {
    if (range.min === range.max) return `${formatNumber(range.max)} in`;

    return `${formatNumber(range.min)}-${formatNumber(range.max)} in`;
  };

  const formatMeasurement = (value) => `${formatNumber(value)} in`;

  const formatHeight = (feet, inches) => `${feet}'${inches}"`;

  const getValues = (form) => {
    const data = new FormData(form);
    const heightFeet = Number.parseInt(data.get('height_feet'), 10);
    const heightInches = Number.parseInt(data.get('height_inches') || '0', 10);

    return {
      profile: data.get('profile'),
      waist: Number.parseFloat(data.get('waist')),
      heightFeet,
      heightInches,
      heightTotal: heightFeet * 12 + heightInches,
      weight: Number.parseFloat(data.get('weight')),
      fit: data.get('fit') || 'relaxed',
      beverages: Number.parseInt(data.get('beverages') || '0', 10),
    };
  };

  const findSizeIndexForMeasurement = (
    measurement,
    value,
    { preferUpperOverlap = false, tolerance = 0 } = {}
  ) => {
    const orderedSizes = preferUpperOverlap ? [...SIZE_CHART].reverse() : SIZE_CHART;
    const matchedSize = orderedSizes.find(
      (size) => value >= size[measurement].min - tolerance && value <= size[measurement].max + tolerance
    );

    if (matchedSize) return SIZE_CHART.findIndex((size) => size.key === matchedSize.key);

    const index = SIZE_CHART.findIndex((size) => value <= size[measurement].max + tolerance);

    return index === -1 ? SIZE_CHART.length - 1 : index;
  };

  const estimateChest = (values) => {
    const settings = PROFILE_SETTINGS[values.profile] || PROFILE_SETTINGS.male;
    const weightAdjustment = clamp(
      (values.weight - settings.weightBaseline) * settings.weightChestFactor,
      settings.chestAdjustmentMin,
      settings.chestAdjustmentMax
    );

    return values.waist + settings.chestOffset + weightAdjustment;
  };

  const estimateInseam = (values) => {
    const settings = PROFILE_SETTINGS[values.profile] || PROFILE_SETTINGS.male;
    const band = settings.inseamBands.find((candidate) => values.heightTotal <= candidate.maxHeight);

    return band ? band.inseam : settings.inseamBands[settings.inseamBands.length - 1].inseam;
  };

  const getGirthNearTopMeasurements = (values, size) => {
    const nearTop = [];

    if (values.waist >= size.waist.max - 0.5) nearTop.push('waist');
    if (values.estimatedChest >= size.chest.max - 0.5) nearTop.push('estimated chest');

    return nearTop;
  };

  const getInseamNearTopMeasurements = (values, size) => {
    const nearTop = [];

    if (values.estimatedInseam >= size.inseam.max - 0.25) nearTop.push('estimated inseam');

    return nearTop;
  };

  const getOutOfRangeMeasurements = (values) => {
    const largest = SIZE_CHART[SIZE_CHART.length - 1];
    const outOfRange = [];

    if (values.waist > largest.waist.max) outOfRange.push('waist');
    if (values.estimatedChest > largest.chest.max) outOfRange.push('estimated chest');
    if (values.estimatedInseam > largest.inseam.max) outOfRange.push('estimated inseam');

    return outOfRange;
  };

  const getBelowChartMeasurements = (values) => {
    const smallest = SIZE_CHART[0];
    const belowChart = [];

    if (values.waist < smallest.waist.min) belowChart.push('waist');
    if (values.estimatedChest < smallest.chest.min) belowChart.push('estimated chest');
    if (values.estimatedInseam < smallest.inseam.min) belowChart.push('estimated inseam');

    return belowChart;
  };

  const getRecommendation = (formValues) => {
    const values = {
      ...formValues,
      estimatedChest: estimateChest(formValues),
      estimatedInseam: estimateInseam(formValues),
    };

    const pressure = [
      {
        label: 'waist',
        index: findSizeIndexForMeasurement('waist', values.waist, { preferUpperOverlap: true }),
      },
      {
        label: 'estimated chest',
        index: findSizeIndexForMeasurement('chest', values.estimatedChest, { preferUpperOverlap: true }),
      },
      {
        label: 'estimated inseam',
        index: findSizeIndexForMeasurement('inseam', values.estimatedInseam, { tolerance: 0.25 }),
      },
    ];

    const baseIndex = Math.max(...pressure.map((item) => item.index));
    const baseSize = SIZE_CHART[baseIndex];
    const girthNearTop = getGirthNearTopMeasurements(values, baseSize);
    const inseamNearTop = getInseamNearTopMeasurements(values, baseSize);
    const nearTop = [...girthNearTop, ...inseamNearTop];
    const waistNearTop = girthNearTop.includes('waist');
    const chestNearTop = girthNearTop.includes('estimated chest');
    const outOfRange = getOutOfRangeMeasurements(values);
    const belowChart = getBelowChartMeasurements(values);
    const cargoNudge =
      values.fit === 'relaxed' &&
      values.beverages >= 8 &&
      girthNearTop.length > 0 &&
      baseIndex < SIZE_CHART.length - 1;
    const fitNudge =
      !cargoNudge &&
      values.fit === 'relaxed' &&
      (waistNearTop || (baseIndex <= 1 && chestNearTop)) &&
      baseIndex < SIZE_CHART.length - 1;
    const finalIndex = fitNudge || cargoNudge ? baseIndex + 1 : baseIndex;

    return {
      values,
      pressure,
      size: SIZE_CHART[finalIndex],
      baseSize,
      fitNudge,
      cargoNudge,
      nearTop,
      girthNearTop,
      inseamNearTop,
      cargoWarning: values.fit === 'snug' && values.beverages >= 8,
      outOfRange,
      belowChart,
    };
  };

  const getPressureSummary = (recommendation) => {
    const primary = recommendation.pressure
      .filter((item) => item.index === recommendation.pressure.reduce((max, candidate) => Math.max(max, candidate.index), 0))
      .map((item) => item.label);

    return primary.length > 0 ? primary.join(', ') : 'the chart';
  };

  const buildResultCopy = (recommendation) => {
    if (recommendation.outOfRange.length > 0) {
      return `${recommendation.size.label} is the largest listed size. Your ${recommendation.outOfRange.join(
        ', '
      )} sits above the chart, so use this as the closest starting point and double-check the full size chart.`;
    }

    if (recommendation.cargoNudge) {
      return `Best guess: ${recommendation.baseSize.label} from the chart, bumped to ${recommendation.size.label} for a relaxed fit plus mountain cargo.`;
    }

    if (recommendation.fitNudge) {
      return `Best guess: ${recommendation.baseSize.label} from the chart, bumped to ${recommendation.size.label} because you asked for a relaxed fit and your ${recommendation.girthNearTop.join(
        ', '
      )} is near the top of the range.`;
    }

    if (recommendation.belowChart.length > 0) {
      return `${recommendation.size.label} is the closest listed size. A few estimates sit below the chart, so expect a roomier fit.`;
    }

    return `Best guess based on your ${getPressureSummary(recommendation)}. We anchored on waist and estimated the rest from height, weight, and profile.`;
  };

  const setResultDetails = (list, recommendation) => {
    const { values, size } = recommendation;
    const detailRows = [
      `Waist: ${formatMeasurement(values.waist)} against ${size.label} range ${formatRange(size.waist)}`,
      `Estimated chest: about ${formatMeasurement(values.estimatedChest)} against ${size.label} range ${formatRange(
        size.chest
      )}`,
      `Estimated inseam: about ${formatMeasurement(values.estimatedInseam)} from ${formatHeight(
        values.heightFeet,
        values.heightInches
      )} height`,
      `Fit preference: ${values.fit === 'relaxed' ? 'Relaxed / baggy' : 'Snug / contoured'}`,
      `Beverage cargo: ${values.beverages || 0}`,
    ];

    if (recommendation.cargoWarning) {
      detailRows.push('Cargo note: snug fit plus heavy pocket cargo may feel packed.');
    }

    if (recommendation.inseamNearTop.length > 0 && recommendation.girthNearTop.length === 0) {
      detailRows.push('Length note: height is driving this recommendation more than waist or chest.');
    }

    list.replaceChildren();

    detailRows.forEach((text) => {
      const item = document.createElement('li');
      item.textContent = text;
      list.append(item);
    });
  };

  const getSizeAliases = (sizeKey) => VARIANT_SIZE_ALIASES[sizeKey] || [normalize(sizeKey)];

  const getSizeByKey = (sizeKey) => SIZE_CHART.find((size) => size.key === sizeKey);

  const optionLooksLikeSize = (container) => {
    const label = container.querySelector('legend, label');
    return label && normalize(label.textContent).includes('size');
  };

  const radioMatchesSize = (radio, aliases) => aliases.includes(normalize(radio.value));

  const selectMatchesSize = (option, aliases) => {
    const value = normalize(option.value || option.textContent);
    const text = normalize(option.textContent);

    return aliases.some((alias) => value === alias || text === alias || text.endsWith(` ${alias}`));
  };

  const isRadioAvailable = (radio) =>
    !radio.disabled && !radio.classList.contains('disabled') && radio.getAttribute('aria-disabled') !== 'true';

  const isSelectOptionAvailable = (option) => {
    const text = normalize(option.textContent);

    return (
      !option.disabled &&
      !option.classList.contains('disabled') &&
      !text.includes('unavailable') &&
      !text.includes('sold out')
    );
  };

  const findSizeRadio = (sizeKey) => {
    const aliases = getSizeAliases(sizeKey);
    const optionGroups = Array.from(document.querySelectorAll('variant-selects fieldset'));

    for (const group of optionGroups) {
      if (!optionLooksLikeSize(group)) continue;

      const match = Array.from(group.querySelectorAll('input[type="radio"]')).find((radio) =>
        radioMatchesSize(radio, aliases)
      );

      if (match) return match;
    }

    return null;
  };

  const findSizeSelect = (sizeKey) => {
    const aliases = getSizeAliases(sizeKey);
    const optionGroups = Array.from(document.querySelectorAll('variant-selects .product-form__input--dropdown'));

    for (const group of optionGroups) {
      if (!optionLooksLikeSize(group)) continue;

      const select = group.querySelector('select');
      const option = select
        ? Array.from(select.options).find((candidate) => selectMatchesSize(candidate, aliases))
        : null;

      if (select && option) return { select, option };
    }

    return null;
  };

  const getProductSizeAvailability = (sizeKey) => {
    const radio = findSizeRadio(sizeKey);

    if (radio) {
      return {
        exists: true,
        available: isRadioAvailable(radio),
      };
    }

    const dropdown = findSizeSelect(sizeKey);

    if (dropdown) {
      return {
        exists: true,
        available: isSelectOptionAvailable(dropdown.option),
      };
    }

    return {
      exists: false,
      available: false,
    };
  };

  const getAvailableSuggestionOrder = (sizeKey) => {
    const startIndex = SIZE_CHART.findIndex((size) => size.key === sizeKey);
    const order = [];

    if (startIndex === -1) return order;

    for (let offset = 0; offset < SIZE_CHART.length; offset += 1) {
      const larger = startIndex + offset;
      const smaller = startIndex - offset;

      if (larger < SIZE_CHART.length) order.push(SIZE_CHART[larger]);
      if (offset > 0 && smaller >= 0) order.push(SIZE_CHART[smaller]);
    }

    return order;
  };

  const getAvailableSizeSuggestion = (sizeKey) => {
    const ideal = getSizeByKey(sizeKey);
    const idealAvailability = getProductSizeAvailability(sizeKey);

    if (idealAvailability.available) {
      return {
        ideal,
        suggested: ideal,
        idealAvailable: true,
        fallbackDirection: 'ideal',
        hasSizeOption: idealAvailability.exists,
      };
    }

    const fallbackOptions = getAvailableSuggestionOrder(sizeKey).slice(1);
    const fallback = fallbackOptions.find((size) => getProductSizeAvailability(size.key).available);
    const idealIndex = SIZE_CHART.findIndex((size) => size.key === sizeKey);
    const fallbackDirection =
      fallback && SIZE_CHART.findIndex((size) => size.key === fallback.key) > idealIndex ? 'larger' : 'smaller';

    return {
      ideal,
      suggested: fallback || null,
      idealAvailable: false,
      fallbackDirection: fallback ? fallbackDirection : 'none',
      hasSizeOption:
        idealAvailability.exists || SIZE_CHART.some((size) => getProductSizeAvailability(size.key).exists),
    };
  };

  const selectProductSize = (sizeKey) => {
    const radio = findSizeRadio(sizeKey);

    if (radio && isRadioAvailable(radio)) {
      radio.click();
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    const dropdown = findSizeSelect(sizeKey);

    if (dropdown && isSelectOptionAvailable(dropdown.option)) {
      dropdown.select.value = dropdown.option.value;
      dropdown.select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  };

  const initFinder = (finder) => {
    if (finder.dataset.shredSizeFinderReady === 'true') return;

    const form = finder.querySelector('[data-shred-size-form]');
    const result = finder.querySelector('[data-shred-size-result]');
    const resultSize = finder.querySelector('[data-shred-size-result-size]');
    const resultCopy = finder.querySelector('[data-shred-size-result-copy]');
    const resultDetails = finder.querySelector('[data-shred-size-result-details]');
    const selectButton = finder.querySelector('[data-shred-size-select]');
    const selectStatus = finder.querySelector('[data-shred-size-select-status]');

    if (!form || !result || !resultSize || !resultCopy || !resultDetails) return;

    let currentRecommendation = null;
    let currentAvailableSuggestion = null;

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const recommendation = getRecommendation(getValues(form));
      const availableSuggestion = getAvailableSizeSuggestion(recommendation.size.key);
      currentRecommendation = recommendation;
      currentAvailableSuggestion = availableSuggestion;

      resultSize.textContent = recommendation.size.label;
      resultCopy.textContent = buildResultCopy(recommendation);
      setResultDetails(resultDetails, recommendation);
      result.hidden = false;

      if (selectButton) {
        selectButton.hidden = !availableSuggestion.suggested;
        selectButton.classList.toggle(
          'shred-size-finder__select--fallback',
          Boolean(availableSuggestion.suggested && !availableSuggestion.idealAvailable)
        );
        const selectButtonLabel = selectButton.querySelector('[data-shred-size-select-label]');

        if (selectButtonLabel) {
          selectButtonLabel.textContent =
            availableSuggestion.suggested && !availableSuggestion.idealAvailable
              ? 'Select Next Best Size'
              : selectButton.dataset.defaultLabel || 'Select This Size';
        }
      }

      if (selectStatus) {
        if (!availableSuggestion.hasSizeOption) {
          selectStatus.textContent = '';
        } else if (availableSuggestion.idealAvailable) {
          selectStatus.textContent = `${recommendation.size.label} is available in this suit.`;
        } else if (availableSuggestion.suggested) {
          selectStatus.textContent =
            availableSuggestion.fallbackDirection === 'smaller'
              ? `${recommendation.size.label} looks sold out in this suit. ${availableSuggestion.suggested.label} is available, but sizing down is not ideal for outerwear and may feel tight through layers or movement.`
              : `${recommendation.size.label} looks sold out in this suit. Next best available pick: ${availableSuggestion.suggested.label}.`;
        } else {
          selectStatus.textContent = `Sorry, ${recommendation.size.label} looks sold out and we are not seeing another available size in this suit. Check out other Shred Suits for a better match.`;
        }
      }
    });

    if (selectButton) {
      selectButton.addEventListener('click', () => {
        if (!currentRecommendation || !currentAvailableSuggestion || !selectStatus) return;

        const sizeToSelect = currentAvailableSuggestion.suggested || currentRecommendation.size;
        const selected = selectProductSize(sizeToSelect.key);
        selectStatus.textContent = selected
          ? `${sizeToSelect.label} selected.`
          : `Choose ${sizeToSelect.label} from the size picker above.`;
      });
    }

    finder.dataset.shredSizeFinderReady = 'true';
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-shred-size-finder]').forEach(initFinder);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
